/* The print job card, bottom left on every page (desktop PrintJobToast) + short notices
 * (the desktop's showWarning / showError toasts), bottom right. */
import { useState } from 'react';
import { Button, Icon, ProgressBar } from '../../ui/index.jsx';
import { usePrintStore, viewOf } from './PrintStore.jsx';
import './print.css';
import { useTranslation } from 'react-i18next';

const ACTIVE = new Set(['preparing', 'sending', 'running']);

export function PrintNotices() {
  const { t } = useTranslation('bv');
  const s = usePrintStore();
  if (!s || !s.notices.length) return null;
  return (
    <div className="bv-notices" aria-live="polite">
      {s.notices.map((n) => (
        <div key={n.id} className={`bv-notice bv-notice--${n.tone}`} role="status">
          <span>{n.text}</span>
          <button type="button" aria-label={t('common.close')} onClick={() => s.dropNotice(n.id)}><Icon name="x" size={16} /></button>
        </div>
      ))}
    </div>
  );
}

export default function PrintJobToast() {
  const { t } = useTranslation('bv');
  const s = usePrintStore();
  const [listOpen, setListOpen] = useState(false);
  if (!s || !s.toastOpen || !s.jobs.length) return null;
  const { jobs } = s;
  const failed = jobs.find((j) => j.status === 'failed' && !j.resolved);
  const active = jobs.find((j) => ACTIVE.has(j.status));
  const queued = jobs.filter((j) => j.status === 'queued');
  const shown = failed || active || queued[0] || [...jobs].reverse().find((j) => !j.dismissed) || jobs[jobs.length - 1];
  const v = viewOf(shown, s.online);
  const tone = v.error ? 'is-failed' : v.tone === 'coral' || v.ack || (v.tone === 'yellow' && !v.terminal) ? 'is-attention' : v.terminal && v.tone === 'green' ? 'is-done' : '';
  const running = !!active;
  const attention = shown.status === 'running' && (v.tone === 'coral' || (!v.canCancel && !v.ack && !v.terminal && /screen/i.test(v.subtitle)));
  const showPct = !v.terminal && !failed;
  const name = [shown.name, shown.total > 1 ? t('print.toast.copyOf', { n: shown.n, total: shown.total }) : null, shown.printer && shown.printer.model].filter(Boolean).join(' · ');
  return (
    <div className={`bv-jobcard ${tone}`} role="status" aria-live="polite" data-testid="job-toast">
      <div className="bv-jobcard__row">
        <span className="bv-jobcard__thumb">{shown.thumb ? <img src={shown.thumb} alt="" /> : <Icon name={shown.kind === 'nest' ? 'layers' : 'print'} size={26} />}</span>
        <div className="bv-jobcard__body">
          <div className="bv-jobcard__head">
            <span className="bv-jobcard__title">{v.title}</span>
            {jobs.length > 1 && (
              <button type="button" className="bv-jobcard__badge" onClick={() => setListOpen((o) => !o)} aria-expanded={listOpen}>
                {t('print.toast.nOf', { n: jobs.indexOf(shown) + 1, total: jobs.length })}
              </button>
            )}
            {!running && !failed && <button type="button" className="bv-jobcard__close" aria-label={t('common.close')} onClick={s.dismissToast}><Icon name="x" size={16} /></button>}
          </div>
          <div className="bv-jobcard__name">{name}</div>
          <div className="bv-jobcard__sub">{v.subtitle}</div>
        </div>
      </div>
      {showPct && <ProgressBar value={v.pct == null ? 0 : v.pct} label={t('print.toast.progress')} className={`${v.pct == null ? 'is-indeterminate' : ''}${attention ? ' is-amber' : ''}`} />}
      <div className="bv-jobcard__actions">
        {failed ? (
          <>
            <Button size="sm" variant="coral" onClick={s.retryFailed}>{t('print.toast.retry')}</Button>
            {queued.length ? <Button size="sm" onClick={s.skipFailed}>{t('print.toast.skipNext')}</Button> : <Button size="sm" onClick={() => { s.skipFailed(); }}>{t('common.dismiss')}</Button>}
          </>
        ) : v.ack ? (
          <Button size="sm" variant="green" onClick={s.acknowledgeActive}>{t('common.ok')}</Button>
        ) : (
          <>
            {attention && <Button size="sm" title={t('print.toast.detachTitle')} onClick={s.detachActive}>{t('print.toast.removeFromQueue')}</Button>}
            {active && v.canCancel && <Button size="sm" variant="coral" disabled={!!active.cancelling} onClick={s.cancelActive}>{t('print.toast.cancelPrint')}</Button>}
            {queued.length > 0 && jobs.length <= 1 && <Button size="sm" onClick={() => setListOpen((o) => !o)}>{t('print.inQueue', { count: queued.length })}</Button>}
          </>
        )}
      </div>
      {listOpen && queued.length > 0 && (
        <div className="bv-jobcard__list">
          <div className="bv-jobcard__listhead"><span>{t('print.inQueue', { count: queued.length })}</span><button type="button" className="bv-linkbtn" onClick={s.clearQueued}>{t('print.toast.clearQueue')}</button></div>
          <ol>
            {queued.map((j, i) => (
              <li key={j.id}>
                <span className="bv-jobcard__num">{i + 1}</span>
                <span className="bv-jobcard__mini">{j.thumb ? <img src={j.thumb} alt="" /> : <Icon name="image" size={16} />}</span>
                <span className="bv-jobcard__qname">{j.name}{j.total > 1 ? ` · ${t('print.toast.copyOf', { n: j.n, total: j.total })}` : ''}</span>
                <button type="button" aria-label={t('print.toast.removeFromQueue')} title={t('print.toast.removeFromQueue')} onClick={() => s.removeQueued(j.id)}><Icon name="x" size={14} /></button>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
